import fetchWithRetry from './fetchWithRetry';
import type RequestOptions from './RequestOptions';
import type MailboxRequestOptions from './MailboxRequestOptions';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { getB2ServiceEndpoint } from 'owa-b2-flight/lib/getB2ServiceEndpoint';
import { type B2ServiceApiMode } from 'owa-b2-flight/lib/B2ServiceApiMode';
import { logStartUsage } from 'owa-analytics-start/lib/logStartUsage';
import getModuleContextMailboxInfo from 'owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo';
import { makeServiceRequestA } from './ServiceRequest';
import { compareB2ServiceResponses } from './compareB2ServiceResponses';

export function makeServiceRequestB2<T>(
    actionName: string,
    endpoint: string,
    parameters: any,
    b2ServiceApiMode: B2ServiceApiMode,
    options?: RequestOptions | MailboxRequestOptions
): Promise<T> {
    if (b2ServiceApiMode === 1) {
        const owaRequest = makeServiceRequestA<T>(actionName, endpoint, parameters, options);
        const b2ServiceRequest = fetchB2Service<T>(
            actionName,
            endpoint,
            parameters,
            false /* fallback */,
            options
        );
        observeShadowParity(actionName, owaRequest, b2ServiceRequest, options);

        return owaRequest;
    }
    return fetchB2Service<T>(actionName, endpoint, parameters, true /* fallback */, options);
}

function observeShadowParity<T>(
    actionName: string,
    owaRequest: Promise<T>,
    b2ServiceRequest: Promise<T>,
    options?: RequestOptions | MailboxRequestOptions
): void {
    void Promise.allSettled([owaRequest, b2ServiceRequest]).then(([owaResult, b2ServiceResult]) => {
        const mailboxInfo = options?.mailboxInfo ?? getModuleContextMailboxInfo();
        if (owaResult.status === 'rejected' || b2ServiceResult.status === 'rejected') {
            logStartUsage('B2ServiceShadowRequestFailed', mailboxInfo, {
                actionName,
                owaSucceeded: owaResult.status === 'fulfilled',
                b2ServiceSucceeded: b2ServiceResult.status === 'fulfilled',
            });
            return;
        }

        const comparison = compareB2ServiceResponses(owaResult.value, b2ServiceResult.value);
        logStartUsage('B2ServiceShadowParity', mailboxInfo, {
            actionName,
            isMatch: comparison.isMatch,
            differenceKind: 'differenceKind' in comparison ? comparison.differenceKind : 'None',
        });
    });
}

function fetchB2Service<T>(
    actionName: string,
    endpoint: string,
    parameters: any,
    fallback: boolean,
    options?: RequestOptions | MailboxRequestOptions
) {
    const b2ServiceEndpoint = getB2ServiceEndpoint(endpoint);
    const wantJsonResponse =
        !options?.returnResponseHeaders && !options?.returnFullResponseOnSuccess;
    const b2ServiceOptions: RequestOptions | MailboxRequestOptions = {
        ...options,
        returnResponseHeaders: true,
    };
    // We know this is a owaB2Service request so add x-responseorigin OwaB2Service in case we get a 404 or for whatever reason x-responseorigin isn't sent in the service response header.
    b2ServiceOptions.datapoint ??= {};
    b2ServiceOptions.datapoint.customData ??= {};
    b2ServiceOptions.datapoint.customData.responseOrigin = 'OwaB2Service';
    return fetchWithRetry(actionName, b2ServiceEndpoint, 1, b2ServiceOptions, parameters)
        .then(response => {
            if (typeof response.status === 'number' && !isSuccessStatusCode(response.status)) {
                throw new Error('B2Service response did not succeed');
            }
            if (wantJsonResponse) {
                return response.json();
            }
            return Promise.resolve(response);
        })
        .then(response => response)
        .catch(error => {
            if (fallback) {
                // TODO : Add telemetry here fallback observed for B2 failures
                const fallbackOptions: RequestOptions | MailboxRequestOptions = { ...options };
                fallbackOptions.datapoint ??= {};
                fallbackOptions.datapoint.customData ??= {};
                fallbackOptions.datapoint.customData.B2ServiceFallback = true;

                return makeServiceRequestA<T>(actionName, endpoint, parameters, fallbackOptions);
            }
            throw error;
        });
}
